/**
 * Réglages de l'application, persistés en IndexedDB.
 *
 * Deux familles pour l'instant :
 *  - `swipe` : quelle action déclenche chacun des quatre balayages de ligne.
 *    Le geste vit dans `gestures/swipeRow.ts`, qui ne connaît plus que des
 *    positions (court/long, gauche/droite) ; c'est la Boîte qui traduit une
 *    position en action, d'après ce réglage.
 *  - `haptic` : le retour vibratoire des gestes, lu par `gestures/haptic.ts`.
 *
 * IndexedDB plutôt que localStorage : ces valeurs ne sont lues qu'après le
 * premier rendu, et l'app y stocke déjà ses caches.
 */

import { get, set } from "idb-keyval";

const KEY = "mailfox:settings:v1";

/** Ce qu'un balayage peut déclencher. Limité à ce que l'app sait faire. */
export type SwipeAction =
  | "archive"
  | "later"
  | "snooze"
  | "read"
  | "reply"
  | "label";

export type Gesture =
  | "shortLeft"
  | "longLeft"
  | "shortRight"
  | "longRight"
  | "longPress";

export const SWIPE_ACTION_LABELS: Record<SwipeAction, string> = {
  archive: "Archiver",
  later: "À traiter",
  snooze: "Reporter",
  read: "Lu / non lu",
  reply: "Répondre",
  label: "Label",
};

export const GESTURE_LABELS: Record<Gesture, string> = {
  shortLeft: "Balayage court à gauche",
  longLeft: "Balayage long à gauche",
  shortRight: "Balayage court à droite",
  longRight: "Balayage long à droite",
  longPress: "Appui long sur un fil",
};

export const GESTURE_ORDER: Gesture[] = [
  "shortLeft",
  "longLeft",
  "shortRight",
  "longRight",
  "longPress",
];

interface Settings {
  swipe: Record<Gesture, SwipeAction>;
  haptic: boolean;
  /** Le pixel de suivi est-il posé par défaut sur les envois depuis la PWA ? */
  trackByDefault: boolean;
  /** Garde-t-on la liste des fils lisible hors ligne (cache IndexedDB) ? */
  offlineCache: boolean;
  loaded: boolean;
}

const DEFAULTS: Omit<Settings, "loaded"> = {
  swipe: {
    shortLeft: "archive",
    longLeft: "later",
    shortRight: "read",
    longRight: "snooze",
    longPress: "label",
  },
  haptic: true,
  trackByDefault: true,
  offlineCache: true,
};

export const settingsState = $state<Settings>({
  swipe: { ...DEFAULTS.swipe },
  haptic: DEFAULTS.haptic,
  trackByDefault: DEFAULTS.trackByDefault,
  offlineCache: DEFAULTS.offlineCache,
  loaded: false,
});

/** À appeler une fois au boot. */
export async function loadSettings(): Promise<void> {
  if (settingsState.loaded) return;
  try {
    const v = await get<Partial<Settings>>(KEY);
    if (v) {
      // Fusion avec les défauts : un réglage ajouté plus tard ne doit pas
      // rester `undefined` chez qui a déjà une préférence enregistrée.
      settingsState.swipe = { ...DEFAULTS.swipe, ...(v.swipe ?? {}) };
      settingsState.haptic = v.haptic ?? DEFAULTS.haptic;
      settingsState.trackByDefault = v.trackByDefault ?? DEFAULTS.trackByDefault;
      settingsState.offlineCache = v.offlineCache ?? DEFAULTS.offlineCache;
    }
  } catch (e) {
    console.warn("loadSettings failed:", e);
  }
  settingsState.loaded = true;
}

async function persist(): Promise<void> {
  try {
    await set(KEY, {
      swipe: $state.snapshot(settingsState.swipe),
      haptic: settingsState.haptic,
      trackByDefault: settingsState.trackByDefault,
      offlineCache: settingsState.offlineCache,
    });
  } catch (e) {
    console.warn("persist settings failed:", e);
  }
}

export function setSwipe(gesture: Gesture, action: SwipeAction): void {
  settingsState.swipe = { ...settingsState.swipe, [gesture]: action };
  void persist();
}

export function setHaptic(on: boolean): void {
  settingsState.haptic = on;
  void persist();
}

export function setTrackByDefault(on: boolean): void {
  settingsState.trackByDefault = on;
  void persist();
}

export function setOfflineCache(on: boolean): void {
  settingsState.offlineCache = on;
  void persist();
}
